import { useContext, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import SessionContext from '../../store';
import UIContext from '../../store/ui';

/**
 * Componente para proteger rutas según autenticación y roles
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Componente a renderizar si tiene acceso
 * @param {string[]} props.allowedRoles - Roles permitidos para acceder (opcional)
 */
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
    const { session, restoreSession } = useContext(SessionContext);
    const { setMenuByUserType, menu } = useContext(UIContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [isChecking, setIsChecking] = useState(true);
    const [currentSession, setCurrentSession] = useState(null);

    useEffect(() => {
        // Intentar restaurar sesión si no hay token en el estado
        if (!session.token) {
            const restored = restoreSession((userType) => {
                // Restaurar menú según el tipo de usuario
                setMenuByUserType(userType);
            });
            setCurrentSession(restored);
        } else {
            setCurrentSession(session);
            // Si hay sesión pero no hay menú, restaurarlo
            if (menu.length === 0) {
                setMenuByUserType(session.type);
            }
        }
        setIsChecking(false);
    }, [session, restoreSession, setMenuByUserType, menu.length]);

    // Verificar si requiere cambio de contraseña
    useEffect(() => {
        const activeSession = currentSession || session;
        if (activeSession && activeSession.token && activeSession.password_update_required && location.pathname !== '/change-password-required') {
            navigate('/change-password-required', { replace: true });
        }
    }, [currentSession, session, navigate, location.pathname]);

    // Mientras verifica la sesión, no renderiza nada
    if (isChecking) {
        return null;
    }

    const activeSession = currentSession || session;

    // Si no hay sesión, redirigir al login
    if (!activeSession || !activeSession.token) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    // Si hay roles permitidos especificados, verificar si el usuario tiene acceso
    if (allowedRoles.length > 0 && !allowedRoles.includes(activeSession.type)) {
        // Redirigir a la página por defecto del operador
        return <Navigate to="/tx" replace />;
    }

    return children;
};

export default ProtectedRoute;